import {useCompany, useGetImage, useTemplate} from '@api'
import {FormFieldType, ImageType, NavigationPathKey, TranslationKey} from '@enums'
import {handleNavigateAtom, pageLinksAtom} from '@state'

import {useEffect, useMemo, useState} from 'react'

import {addTopRightPageNumbers, FormFieldItem, generatePdf, useViewNavigate} from 'common'
import {useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'
import {useParams} from 'react-router-dom'

export function useContractTemplateFormModel() {
  const handleNavigate = useSetAtom(handleNavigateAtom)
  const setTitleLinks = useSetAtom(pageLinksAtom)
  const [pdfUrl, setPdfUrl] = useState('')
  const navigate = useViewNavigate()
  const {t} = useTranslation()
  const {id} = useParams()

  const {data: contractTemplateData} = useTemplate(id)
  const {data: companyData} = useCompany(contractTemplateData?.companyId || '')

  const {data: imageUrl} = useGetImage(ImageType.COMPANY, contractTemplateData?.companyId || '')

  useEffect(
    () =>
      setTitleLinks([
        {title: t(TranslationKey['Admin panel']), link: NavigationPathKey.ADMIN_PANEL},
        {title: t(TranslationKey.Contracts), link: NavigationPathKey.CONTRACT_DASHBOARD},
        {title: t(TranslationKey['Contract template search']), link: NavigationPathKey.CONTRACT_TEMPLATE_SEARCH},
        {title: t(TranslationKey['Contract template view'])},
      ]),
    [],
  )

  /**
   * Contract Template form fields configuration
   */
  const contractTemplateFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Contract template name']),
      text: contractTemplateData?.name || '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Description),
      text: contractTemplateData?.description || '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Contract Type']),
      text: contractTemplateData?.contractType || '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Company),
      text: companyData?.name || '-',
    },
  ]

  /**
   * A function handling Discard button click
   */
  const onDiscard = (): void => {
    handleNavigate(() => navigate(-1))
  }

  const htmlContent = useMemo(() => contractTemplateData?.htmlContent, [contractTemplateData])

  const mockPreviewData: Record<string, string> = useMemo(
    () => ({
      COMPANY_NAME: 'My Company Ltd.',
      COMPANY_LOGO: imageUrl || '',
      CREATION_DATE: '2025-07-28',
      CONTRACT_NAME: 'Contract #12345',
      CONTRACT_CODE: 'CODE123',
      CUSTOMER_FIRST_NAME: 'John',
      CUSTOMER_LAST_NAME: 'Doe',
      CUSTOMER_EMAIL: 'john.doe@example.com',
      CUSTOMER_PHONE: '+1234567890',
      CUSTOMER_STREET: '123 Main St',
      CUSTOMER_ZIP_CODE: '12345',
      CUSTOMER_CITY: 'Sample City',
      DEALER_VAT_NUMBER: 'DE123456789',
      DEALER_COMPANY_NO: '987654321',
      DEALER_NO: '123456',
      DEALER_ADDRESS: '456 Dealer St',
      DEALER_ZIP_CODE: '54321',
      DEALER_CITY: 'Dealer City',
      PRODUCT_QTY: '10',
      PRODUCT_NAME: 'Sample Product',
      PRODUCT_SERIAL_NO: 'SN1234567890',
      PRODUCT_INSURANCE: 'Full Coverage',
      PRODUCT_SERVICE: '2 Years Warranty',
      PRODUCT_RESIDUAL: '$500',
      PRODUCT_MONTHLY_FEE: '$50',
      PRODUCT_TOTAL_PRICE: '$500',
      TOTAL_LENGTH: '24 Months',
      TOTAL_MONTHLY_FEE: '$50',
      TOTAL_TOTAL: '$1200',
      TERMS_DISCLAIMER: 'Terms and conditions apply. Subject to change.',
    }),
    [imageUrl],
  )

  const replaceTemplateKeys = async () => {
    const htmlData = htmlContent?.replace(/\$\{(.*?)\}/g, (_, key) => {
      const cleanKey = key.trim()

      return mockPreviewData[cleanKey] || `\${${cleanKey}}`
    })
    const doc = await generatePdf(htmlData || '')
    const pdfWithNumbers = addTopRightPageNumbers(
      doc,
      (i, total) => `${t(TranslationKey.Page)} ${i} ${t(TranslationKey.of)} ${total}`,
    )
    const pdfBlob = pdfWithNumbers.output('blob')
    const pdfUrl = URL.createObjectURL(pdfBlob)
    setPdfUrl(pdfUrl)
  }

  useEffect(() => {
    replaceTemplateKeys()
  }, [htmlContent, mockPreviewData])

  /**
   * A function handling the Back button click
   */
  const onPressBack = (): void => {
    navigate(NavigationPathKey.CONTRACT_TEMPLATE_SEARCH)
  }

  /**
   * A function handling Edit button click
   */
  const onPressEdit = (): void => {
    navigate(`${NavigationPathKey.CONTRACT_TEMPLATE_CREATION}/${id}`)
  }

  return {
    onDiscard,
    htmlContent,
    t,
    contractTemplateData,
    mockPreviewData,
    pdfUrl,
    contractTemplateFormFields,
    onPressBack,
    onPressEdit,
    id,
  }
}
